import { lazy, Suspense, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/api/errors";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import { FileDropZone } from "./file-drop-zone";
import {
  readMarkdownFile,
  type SelectedMarkdownFile,
} from "./read-markdown-file";
import { useCreateModelFile } from "./use-create-model-file";
import { README_FILE, useProcessModelFile } from "./use-process-model";
import { useSaveModelFile } from "./use-save-model-file";

/**
 * 説明を開いたときだけ Markdown の描画部を読み込む（設計画面以外では要らない）。
 */
const MarkdownView = lazy(() =>
  import("@/components/markdown/markdown-view").then((m) => ({
    default: m.MarkdownView,
  })),
);

export type ModelReadmeProps = {
  readonly processModelId: string;
};

/**
 * モデルの説明（README.md）の閲覧と編集。
 *
 * 図だけでは「なぜこの分岐があるのか」「誰が使うのか」が残らない。標準UI が扱うのと
 * 同じファイルを読み書きするので、どちらで書いても同じものが見える。
 *
 * 表示は Markdown を整形して出す（記法の記号は見せない）。生の記述は編集時にだけ
 * 見せる —— 読む人が知りたいのは内容で、記法ではないため。
 *
 * 編集欄には .md のファイルを読み込める（AI が書き出した README.md をそのまま入れる
 * ため）。読み込むのは編集欄の中身までで、確定は「説明を保存」のまま。選んだファイルの
 * 名前は使わない（説明は常に README.md）。
 */
export function ModelReadme({ processModelId }: ModelReadmeProps) {
  const { t } = useTranslation("common");
  const file = useProcessModelFile(processModelId, README_FILE);
  const [draft, setDraft] = useState<string | null>(null);
  /** 編集欄に読み込んだファイル（名前の表示と、使えなかった理由に使う）。 */
  const [loaded, setLoaded] = useState<SelectedMarkdownFile | null>(null);
  /** 選ぶたびに振る番号。読み終わる順が逆転しても最後に選んだものだけ使う。 */
  const latestPick = useRef(0);
  const labelId = useId();

  const save = useSaveModelFile();
  const create = useCreateModelFile();
  const pending = save.isPending || create.isPending;

  // 404（未作成）とその他の失敗を分ける。未作成なら作成を促す
  const isMissing =
    file.isError && file.error instanceof ApiError && file.error.status === 404;
  const contents = file.data?.contents ?? "";

  if (file.isPending) return <Skeleton className="h-24 w-full" />;

  // 取得できなかった（404 以外）。空の説明と取り違えて編集・読み込みさせると、
  // ハッシュ無しの保存で本物を上書きしうるので、その旨だけ出す
  if (file.isError && !isMissing) {
    return (
      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold">
          {t("processDesign.readme.title")}
        </h3>
        <p role="alert" className="text-destructive text-sm">
          {t("state.error")}
        </p>
      </section>
    );
  }

  const isConflict =
    save.isError && save.error instanceof ApiError && save.error.status === 409;

  const openEditor = () => setDraft(isMissing ? "" : contents);
  const closeEditor = () => {
    // 読み込み中に閉じたら、読み終わっても編集欄を開き直さない
    latestPick.current += 1;
    setDraft(null);
    setLoaded(null);
  };

  const loadFromFile = async (picked: File) => {
    latestPick.current += 1;
    const ticket = latestPick.current;
    const result = await readMarkdownFile(picked);
    if (ticket !== latestPick.current) return;

    setLoaded(result);
    // 書きかけがあっても置き換える。保存前に編集欄で見られるので確認は挟まない
    if (result.kind === "ok") setDraft(result.contents);
  };

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">
          {t("processDesign.readme.title")}
        </h3>
        {draft === null ? (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={openEditor}
            >
              {isMissing
                ? t("processDesign.readme.create")
                : t("processDesign.readme.edit")}
            </Button>
            {/* ファイルを選ぶ欄は編集欄の中にある。ここは「そこへ行ける」導線 */}
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={openEditor}
            >
              {t("processDesign.readme.loadFromFile")}
            </Button>
          </div>
        ) : null}
      </div>

      {draft === null ? (
        isMissing ? (
          <p className="text-muted-foreground text-sm">
            {t("processDesign.readme.empty")}
          </p>
        ) : (
          <Suspense
            fallback={
              <pre className="text-sm whitespace-pre-wrap">{contents}</pre>
            }
          >
            <MarkdownView source={contents} />
          </Suspense>
        )
      ) : (
        <div className="flex flex-col gap-2">
          <FileDropZone
            label={t("processDesign.readme.fileLabel")}
            accept=".md"
            hint={t("processDesign.readme.fileHint")}
            selectedName={loaded?.kind === "ok" ? loaded.fileName : null}
            issue={loadIssue(loaded, t)}
            onFiles={(files) => {
              const picked = files[0];
              if (picked !== undefined) void loadFromFile(picked);
            }}
          />

          <label htmlFor={labelId} className="sr-only">
            {t("processDesign.readme.label")}
          </label>
          <textarea
            id={labelId}
            aria-label={t("processDesign.readme.label")}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            rows={12}
            className="border-input bg-background rounded-md border px-2 py-1.5 font-mono text-sm"
          />

          {isConflict ? (
            <p role="alert" className="text-destructive text-sm">
              {t("processDesign.readme.conflict")}
            </p>
          ) : null}
          {(save.isError && !isConflict) || create.isError ? (
            <p role="alert" className="text-destructive text-sm">
              {t("processDesign.readme.saveFailed")}
            </p>
          ) : null}

          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              disabled={pending}
              onClick={() => {
                const onSuccess = closeEditor;
                if (isMissing) {
                  create.mutate(
                    {
                      processModelId,
                      fileName: README_FILE,
                      contents: draft,
                    },
                    { onSuccess },
                  );
                  return;
                }
                save.mutate(
                  {
                    processModelId,
                    fileName: README_FILE,
                    contents: draft,
                    contentsHash: file.data?.contentsHash ?? null,
                  },
                  { onSuccess },
                );
              }}
            >
              {t("processDesign.readme.save")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={pending}
              onClick={closeEditor}
            >
              {t("processDesign.readme.cancel")}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

/** 読み込んだファイルが使えなかった理由。使えたか未選択なら null。 */
function loadIssue(
  loaded: SelectedMarkdownFile | null,
  t: ReturnType<typeof useTranslation<"common">>["t"],
): string | null {
  if (loaded === null || loaded.kind === "ok") return null;
  return t(
    loaded.kind === "not-markdown"
      ? "processDesign.readme.notMarkdown"
      : "processDesign.fileDrop.unreadable",
  );
}
